import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import ArrowLeft from 'ext-ui/svg/icon/ArrowLeft'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeedCreate/CardFeedCreate.style'
import ErrorList from 'src/components/ErrorList/ErrorList'
import { useTranslate } from 'src/localization/Trns'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'

import type { CardNetwork } from './CardFeedCreate'

interface StepReviewTermsProps {
  network: CardNetwork | undefined
  termsAccepted: boolean
  onTermsAcceptedChange: (accepted: boolean) => void
  onBack: () => void
  onConfirm: () => void
  isSubmitting: boolean
  submitError: string | undefined
}

const networkDisplayName = (
  network: CardNetwork | undefined,
  translate: (key: string) => string,
): string => {
  if (network === 'visa') return translate('Visa')
  return translate('Mastercard')
}

const StepReviewTerms: React.FC<StepReviewTermsProps> = ({
  network,
  termsAccepted,
  onTermsAcceptedChange,
  onBack,
  onConfirm,
  isSubmitting,
  submitError,
}) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const currentOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const networkName = networkDisplayName(network, translate)

  const handleAcknowledgeToggle = (): void => {
    const next = !termsAccepted
    onTermsAcceptedChange(next)
    analytics.track('card feeds create', {
      action: 'terms acknowledged toggled',
      accepted: next,
    })
  }

  const handleBackClick = (): void => {
    analytics.track('card feeds create', { action: 'back clicked', step: 3 })
    onBack()
  }

  const handleConfirmClick = (): void => {
    analytics.track('card feeds create', {
      action: 'confirm and connect clicked',
      network,
    })
    onConfirm()
  }

  return (
    <div>
      <header css={style.stepHeader}>
        <span css={style.stepEyebrow}>{translate('Step 3 of 3')}</span>
        <p css={style.stepHeading}>
          {translate('Review and accept your enrollment terms')}
        </p>
        <p css={style.stepBody}>
          {translate(
            "A quick review before we connect. These terms describe how Extend handles your card data and what your bank's responsibilities are.",
          )}
        </p>
      </header>

      <section css={style.termsBox} aria-labelledby="enrollment-terms-intro">
        <span id="enrollment-terms-intro" css={style.termsBoxIntro}>
          {translate(
            'By enrolling your company & clicking on "Submit", you hereby agree to the following enrollment terms:',
          )}
        </span>
        <ul css={style.termsBoxList}>
          <li>
            {translate(
              'By sending us the bank feed data, you acknowledge that your company has obtained consent from individual cardholders, as recognized under applicable law, authorizing ${network} to access and share transaction details with Extend and your company to facilitate individual cardholders’ participation in the Extend service; and that individual cardholders may revoke such consent (i.e., opt out of sharing their payment card transaction details) at any time.',
              { network: networkName },
            )}
          </li>
          <li>
            {translate(
              'Allow Extend to create expenses on your behalf and enable program notifications.',
            )}
          </li>
        </ul>
      </section>

      <div css={style.termsAcceptRow} data-testid="card-feed-create-terms-row">
        <Checkbox
          id="card-feed-create-terms-checkbox-input"
          aria-label={translate('I accept the Card Feed Enrollment Terms')}
          checked={termsAccepted}
          onChange={handleAcknowledgeToggle}
          data-testid="card-feed-create-terms-checkbox"
        />
        <label htmlFor="card-feed-create-terms-checkbox-input">
          {translate(
            "I've read and accept the Card Feed Enrollment Terms on behalf of ${organizationName}.",
            {
              organizationName:
                currentOrganization?.name ?? translate('your organization'),
            },
          )}
        </label>
      </div>

      {submitError !== undefined && (
        <div data-testid="card-feed-create-submit-error">
          <ErrorList errors={[submitError]} />
        </div>
      )}

      <div css={style.stepActions}>
        <Button
          variant="text"
          iconLeft={<ArrowLeft aria-hidden="true" />}
          onClick={handleBackClick}
          disabled={isSubmitting}
          data-testid="card-feed-create-back-3"
        >
          {translate('Go Back')}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmClick}
          disabled={!termsAccepted || isSubmitting}
          data-testid="card-feed-create-confirm"
        >
          {isSubmitting
            ? translate('Connecting...')
            : translate('Confirm & Connect')}
        </Button>
      </div>
    </div>
  )
}

export default StepReviewTerms
